/**
 * BalanceCard — month net (income − expense) hero on the home screen.
 *
 * Pixel-faithful port of the dark balance card in
 * `design/project/screens-home.jsx` and `screens-web-shell.jsx`.
 *
 * - Dark `--mocha-600` (mobile) / `--mocha-600` (desktop) ground with a
 *   warm radial highlight.
 * - Big tabular-num display for the net total.
 * - All foreign-currency tx are FX-converted into the primary currency
 *   via `convertMinor` before summing — no per-currency tabs.
 * - A 「上月 +N%」 hint slot is left as an optional prop; if the parent
 *   doesn't compute one we don't render it (avoids fake "+12%" placeholders
 *   leaking from the design source).
 */

import { useMemo, type CSSProperties } from 'react';

import Icon from '@/components/common/Icon';
import { formatAmount } from '@/lib/format/currency';
import { convertMinor } from '@/lib/format/fx';
import type { TransactionRow } from '@/lib/sheets/types';
import { startOfMonth, endOfMonth, formatYmd } from '@/lib/format/date';
import { dayPart } from '@/lib/format/datetime';

export interface BalanceCardProps {
  transactions: TransactionRow[];
  today: Date;
  /** Primary display currency — all txs are FX-converted to this. */
  currency: string;
  /** Optional `+12%` style label (parent-computed). */
  trendLabel?: string;
  /** Direction of the trend chip — `up` shows green ascending arrow,
   *  `down` shows coral descending arrow. Required when `trendLabel`
   *  represents a negative percentage so the visual matches the sign. */
  trendDirection?: 'up' | 'down';
  /** Compact mode — desktop hero is wider, mobile is taller. */
  layout?: 'mobile' | 'desktop';
}

interface MonthTotals {
  net: number;
  expense: number;
  income: number;
}

function computeTotals(
  txs: TransactionRow[],
  today: Date,
  currency: string,
): MonthTotals {
  const start = formatYmd(startOfMonth(today));
  const end = formatYmd(endOfMonth(today));
  let income = 0;
  let expense = 0;
  for (const t of txs) {
    if (!t.date) continue;
    // v5: dayPart strips any THH:MM suffix so the lex-compare lines
    // up against the day-only `start`/`end` boundaries.
    const day = dayPart(t.date);
    if (day < start || day > end) continue;
    const minor = convertMinor(t.amount_minor, t.currency || 'TWD', currency);
    if (t.kind === 'income') income += minor;
    else if (t.kind === 'expense') expense += minor;
  }
  return { net: income - expense, expense, income };
}

/**
 * CJK month names — design source uses 「·四月」 not 「5 月」 in the
 * BalanceCard month suffix. Index 0 is January.
 */
const CJK_MONTHS = [
  '一月', '二月', '三月', '四月', '五月', '六月',
  '七月', '八月', '九月', '十月', '十一月', '十二月',
] as const;

function MonthLabel(d: Date): string {
  return CJK_MONTHS[d.getMonth()] ?? `${d.getMonth() + 1}月`;
}

export default function BalanceCard({
  transactions,
  today,
  currency,
  trendLabel,
  trendDirection = 'up',
  layout = 'mobile',
}: BalanceCardProps): JSX.Element {
  const totals = useMemo(
    () => computeTotals(transactions, today, currency),
    [transactions, today, currency],
  );

  const isDesktop = layout === 'desktop';

  const containerStyle: CSSProperties = {
    background: 'var(--mocha-600)',
    borderRadius: 20,
    padding: isDesktop ? '28px 32px' : '22px 22px 24px',
    boxShadow: 'var(--shadow-card-dark)',
    position: 'relative',
    overflow: 'hidden',
    color: 'var(--fg-on-inverse)',
  };

  return (
    <div style={containerStyle} data-testid="balance-card">
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 88% 18%, rgba(199,154,75,0.16), transparent 55%)',
        }}
      />
      <div style={{ position: 'relative' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              font: '500 11px var(--font-cjk)',
              color: 'var(--fg-on-inverse-2)',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}
          >
            本月結餘 · {MonthLabel(today)}
          </div>
          {trendLabel && (() => {
            // `up` = month net improved → green; `down` = worsened → coral.
            // Color + icon flip together so a negative percentage never
            // ships with a green ascending arrow (visual-vs-sign mismatch
            // confused users into reading downturns as wins).
            const color = trendDirection === 'down' ? '#E58E72' : '#C8D89A';
            const iconName = trendDirection === 'down' ? 'trendingDown' : 'trending';
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  font: '500 11px var(--font-cjk)',
                  color,
                }}
              >
                <Icon name={iconName} size={12} stroke={color} />
                {trendLabel}
              </div>
            );
          })()}
        </div>
        <div
          style={{
            // Net amount — clamp so a `NT$ 12,345,678` figure never
            // overflows on a narrow phone. Lower bound 24px keeps it
            // legible; upper bound 56px (desktop) / 42px (mobile) keeps
            // the design's dramatic hero size on roomy viewports.
            fontWeight: 600,
            fontFamily: 'var(--font-num)',
            fontSize: isDesktop
              ? 'clamp(28px, 5vw, 56px)'
              : 'clamp(24px, 9vw, 42px)',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.02em',
            marginTop: 10,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {formatAmount(totals.net, currency)}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 16,
            marginTop: 14,
            // min-width:0 lets the flex children shrink below their
            // intrinsic content width — the inner amount strings have
            // their own clamp() so this just yields the cell.
            minWidth: 0,
          }}
        >
          <div style={{ flex: '1 1 0', minWidth: 0 }}>
            <div
              style={{
                font: '400 11px var(--font-cjk)',
                color: 'var(--fg-on-inverse-2)',
                marginBottom: 4,
              }}
            >
              支出
            </div>
            <div
              style={{
                fontWeight: 500,
                fontFamily: 'var(--font-num)',
                // Two-line wrap was the bug — small phones with 6+
                // digits split `NT$ 1,234,567` into `NT$ 1,234,` /
                // `567`. clamp() shrinks gracefully; whiteSpace:nowrap
                // forbids the wrap.
                fontSize: isDesktop
                  ? 'clamp(15px, 1.6vw, 22px)'
                  : 'clamp(11px, 3.6vw, 15px)',
                fontVariantNumeric: 'tabular-nums',
                color: 'var(--cream-50)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {formatAmount(totals.expense, currency)}
            </div>
          </div>
          <div style={{ width: 1, background: 'rgba(251,247,241,0.12)', flexShrink: 0 }} />
          <div style={{ flex: '1 1 0', minWidth: 0 }}>
            <div
              style={{
                font: '400 11px var(--font-cjk)',
                color: 'var(--fg-on-inverse-2)',
                marginBottom: 4,
              }}
            >
              收入
            </div>
            <div
              style={{
                fontWeight: 500,
                fontFamily: 'var(--font-num)',
                fontSize: isDesktop
                  ? 'clamp(15px, 1.6vw, 22px)'
                  : 'clamp(11px, 3.6vw, 15px)',
                fontVariantNumeric: 'tabular-nums',
                color: 'var(--cream-50)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {formatAmount(totals.income, currency)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
